import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Tooltip,
} from '@mui/material';
import { useTradeStore } from '../store/tradeStore';
import SellDialog from '../components/SellDialog';
import { getPriceColor, formatPct, formatNumber, formatMoney, formatDate } from '../utils/format';
import { isTradingTime } from '../utils/constants';
import { fetchEnrichedHoldings } from '../api/trades';
import type { EnrichedHolding } from '../types';

/**
 * 持仓监控页
 * 展示持仓列表 + 实时盈亏 + 增强数据（入选价/20日最低/买卖触发价/买入后高低/今日收盘盈亏）+ 手动卖出
 */
export default function PortfolioPage() {
  const { sell, account, fetchAccount } = useTradeStore();
  const [holdings, setHoldings] = useState<EnrichedHolding[]>([]);
  const [loading, setLoading] = useState(false);
  const [sellDialogOpen, setSellDialogOpen] = useState(false);
  const [selectedHolding, setSelectedHolding] = useState<EnrichedHolding | null>(null);
  const timerRef = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await fetchEnrichedHoldings();
      setHoldings(data);
    } catch (e) {
      console.error('获取持仓增强数据失败:', e);
    }
  }, []);

  useEffect(() => {
    const schedule = () => {
      const interval = isTradingTime() ? 10000 : 60000;
      timerRef.current = window.setTimeout(async () => {
        await load();
        schedule();
      }, interval);
    };
    load();
    schedule();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [load]);

  const handleSellClick = (holding: EnrichedHolding) => {
    setSelectedHolding(holding);
    setSellDialogOpen(true);
  };

  const handleSellConfirm = async (holdingId: number, price: number, quantity: number) => {
    await sell(holdingId, price, quantity);
    await load();
    await fetchAccount();
  };

  const th = (label: string, align: 'right' | 'left' | 'center' = 'right') => (
    <TableCell align={align} sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{label}</TableCell>
  );

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
        持仓监控
      </Typography>

      {/* 账户概览 */}
      {account && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">可用资金</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {formatMoney(account.balance)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">持仓市值</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {formatMoney(account.marketValue)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">总资产</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {formatMoney(account.totalAssets)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={3}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary">总盈亏</Typography>
                <Typography variant="h6" sx={{ fontWeight: 600, color: getPriceColor(account.totalProfit ?? 0) }}>
                  {formatMoney(account.totalProfit)}
                  <Typography component="span" variant="body2" sx={{ ml: 1 }}>
                    ({formatPct(account.totalProfitPct)})
                  </Typography>
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* 持仓列表 */}
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 1500 }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell sx={{ fontWeight: 600 }}>代码</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>名称</TableCell>
              {th('买入价')}
              {th('数量')}
              {th('持仓天数')}
              {th('现价')}
              {th('今日涨跌%')}
              {th('盈亏%')}
              {th('盈亏额')}
              {th('入选收盘价')}
              {th('近30日最低')}
              {th('涨卖价')}
              {th('跌卖价')}
              {th('买入后最高')}
              {th('买入后最低')}
              {th('今日收盘盈亏')}
              {th('操作', 'center')}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && holdings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={17} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">加载中...</Typography>
                </TableCell>
              </TableRow>
            ) : holdings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={17} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    暂无持仓
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              holdings.map((h) => (
                <TableRow key={h.id} hover className="stock-row">
                  <TableCell sx={{ fontFamily: 'monospace' }}>{h.code}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {h.displayName || h.name}
                    {h.isAvgLot && (
                      <Tooltip title="补仓笔：该股票原持仓跌≥5%后再次买入；涨到补仓价+2.5%时两笔一起卖出" arrow>
                        <span style={{ marginLeft: 6, fontSize: 11, color: '#ff9800', border: '1px solid #ff9800', borderRadius: 4, padding: '0 4px' }}>补仓</span>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right">{formatNumber(h.buyPrice)}</TableCell>
                  <TableCell align="right">{h.quantity}</TableCell>
                  <TableCell align="right">{h.holdingDays ?? 0}天</TableCell>
                  <TableCell align="right">
                    <span style={{ color: getPriceColor(h.currentProfitPct ?? 0) }}>
                      {formatNumber(h.currentPrice)}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span style={{ color: getPriceColor(h.todayChangePct ?? 0) }}>
                      {formatPct(h.todayChangePct)}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span style={{ color: getPriceColor(h.currentProfitPct ?? 0), fontWeight: 600 }}>
                      {formatPct(h.currentProfitPct)}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span style={{ color: getPriceColor(h.currentProfit ?? 0) }}>
                      {formatMoney(h.currentProfit)}
                    </span>
                  </TableCell>
                  {/* 入选当日收盘价 */}
                  <TableCell align="right">
                    {h.candidatePrice !== undefined ? (
                      <Tooltip title={h.candidateDate ? `入选日 ${h.candidateDate}` : ''} arrow>
                        <span>{formatNumber(h.candidatePrice)}</span>
                      </Tooltip>
                    ) : '--'}
                  </TableCell>
                  {/* 近30日最低价（由 loss_30d_low_stop_days 条件决定） */}
                  <TableCell align="right" sx={{ color: 'text.secondary' }}>
                    {formatNumber(h.lowNDays ?? h.low20)}
                  </TableCell>
                  {/* 上涨卖出价 */}
                  <TableCell align="right">
                    <span style={{ color: '#f44336', fontWeight: 600 }}>
                      {formatNumber(h.sellHigh)}
                    </span>
                  </TableCell>
                  {/* 下跌卖出价 */}
                  <TableCell align="right">
                    <span style={{ color: '#4caf50', fontWeight: 600 }}>
                      {formatNumber(h.sellLow)}
                    </span>
                  </TableCell>
                  {/* 入选后盘中最高价 */}
                  <TableCell align="right">
                    <span style={{ color: getPriceColor(1), fontWeight: h.maxHighSince !== undefined && h.sellHigh !== undefined && h.maxHighSince >= h.sellHigh ? 700 : 400 }}>
                      {formatNumber(h.maxHighSince)}
                    </span>
                  </TableCell>
                  {/* 入选后盘中最低价 */}
                  <TableCell align="right">
                    <span style={{ color: getPriceColor(-1), fontWeight: h.minLowSince !== undefined && h.sellLow !== undefined && h.minLowSince <= h.sellLow ? 700 : 400 }}>
                      {formatNumber(h.minLowSince)}
                    </span>
                  </TableCell>
                  {/* 今日收盘盈亏 */}
                  <TableCell align="right">
                    <span style={{ color: getPriceColor(h.todayCloseProfit ?? 0), fontWeight: 600 }}>
                      {formatMoney(h.todayCloseProfit)}
                      <Typography component="span" variant="caption" sx={{ display: 'block' }}>
                        {formatPct(h.todayCloseProfitPct)}
                      </Typography>
                    </span>
                  </TableCell>
                  <TableCell align="center">
                    <Box
                      component="button"
                      onClick={() => handleSellClick(h)}
                      sx={{
                        border: '1px solid',
                        borderColor: 'error.main',
                        color: 'error.main',
                        bgcolor: 'transparent',
                        borderRadius: 1,
                        px: 1.5,
                        py: 0.25,
                        cursor: 'pointer',
                        fontSize: 12,
                        '&:hover': { bgcolor: 'error.main', color: '#fff' },
                      }}
                    >
                      卖出
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        涨卖价来自盈利条件（买入价 + 阈值%）；跌卖价来自止损条件（启用「近N日最低价止损」时按N日最低价，否则按买入价 - 阈值%）；买入后最高/最低自买入日起计算；今日收盘盈亏盘中为实时估算，收盘后为收盘价结算。
        同一股票多日重复买入时会以「名称+序号」独立展示（如 青岛啤酒2），各自独立核算盈亏与卖出。
      </Typography>

      <SellDialog
        open={sellDialogOpen}
        holding={selectedHolding}
        onClose={() => setSellDialogOpen(false)}
        onConfirm={handleSellConfirm}
      />
    </Box>
  );
}
